import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { RaceContestantDetails } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputSelect,
    DataSourceLookup,
    InputNullableDateTime,
    InputDateTime,
    DateComp,
    InputBool,
    DisplayBool,
    DisplayInfotext,
    InputText,
    ValidatedText,
    SelectCountry,
    InputInt,
    ValidatedInt,
    InputPhone,
    InputEmail,
    DisplaySeparator,
    SelectEventTeam,
} from '@/components';
import { useCommonStore, useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class RaceContestantDetailsGuiDef extends GuiDef<RaceContestantDetails> {
    constructor() {
        super('RaceContestantDetails');

        const Common = storeToRefs(useCommonStore());
        const GendersSource = computed(() => {
            return Common.GendersAsSelectLine.value;
        });
        const ClubsSource = computed(() => {
            return Common.ClubsAsSelectLine.value;
        });
        const TeamsSource = computed(() => {
            return Common.TeamsAsSelectLine.value;
        });
        const EventCommon = storeToRefs(useEventCommonStore());
        const EventTeamsSource = computed(() => {
            return EventCommon.EventTeamsAsSelectLine.value;
        });
        const CategoriesSource = computed(() => {
            return EventCommon.CategoriesAsSelectLine.value;
        });
        const ContestantStatusSource = computed(() => {
            return Common.ContestantStatusAsSelectLine.value;
        });
        const LicencesSource = computed(() => {
            return EventCommon.LicencesAsSelectLine.value;
        });

        const Gender = new FieldDef<RaceContestantDetails>();
        Gender.Group = new GroupDef('row20', '', true);
        Gender.Name = 'Gender';
        Gender.FieldName = 'Gender';
        Gender.DefinedFor = 'Both';
        Gender.TranslationKey = 'racecontestantdetails.gender';
        Gender.Editable = true;
        Gender.Filterable = true;
        Gender.Sortable = true;
        Gender.CssClasses = 'is-lockable';
        Gender.value = (racecontestantdetails) => racecontestantdetails.Gender;
        Gender.setValue = (racecontestantdetails, value) => (racecontestantdetails.Gender = value);
        Gender.DetailEditor = markRaw(InputSelect);
        Gender.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: GendersSource } };
        Gender.CellEditor = markRaw(InputSelect);
        Gender.CellEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: GendersSource }, autoOpen: true };
        // @ts-expect-error @ts-ignore
        Gender.CellRenderer = markRaw(DataSourceLookup);
        Gender.CellRendererProps = { datasource: { mapsource: GendersSource } };
        Gender.ExportValueFormatter = (obj: RaceContestantDetails) => {
            return GendersSource.value.get(obj.Gender)?.text;
        };
        Gender.Validator = null;
        Gender.SortDirection = undefined;
        Gender.cellClassRules = undefined;
        Gender.MinWidth = undefined;
        Gender.MaxWidth = undefined;
        Gender.Tooltip = null;
        this.Fields.push(Gender);

        const BirthdayValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const Birthday = new FieldDef<RaceContestantDetails>();
        Birthday.Group = new GroupDef('row20', '', true);
        Birthday.Name = 'Birthday';
        Birthday.FieldName = 'Birthday';
        Birthday.DefinedFor = 'Both';
        Birthday.TranslationKey = 'racecontestantdetails.birthday';
        Birthday.Editable = true;
        Birthday.Filterable = true;
        Birthday.Sortable = true;
        Birthday.CssClasses = 'is-lockable';
        Birthday.value = (racecontestantdetails) => racecontestantdetails.Birthday;
        Birthday.setValue = (racecontestantdetails, value) => (racecontestantdetails.Birthday = value);
        Birthday.DetailEditor = markRaw(InputNullableDateTime);
        Birthday.DetailEditorProps = { format: 'DATE' as DateType };
        Birthday.CellEditor = markRaw(InputDateTime);
        Birthday.CellEditorProps = { format: 'DATE' as DateType };
        Birthday.CellRenderer = markRaw(DateComp);
        Birthday.CellRendererProps = { format: 'DATE' as DateType };
        Birthday.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        Birthday.SortDirection = undefined;
        Birthday.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(BirthdayValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(BirthdayValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Birthday.MinWidth = 100;
        Birthday.MaxWidth = undefined;
        Birthday.Tooltip = null;
        this.Fields.push(Birthday);

        const Anonymous = new FieldDef<RaceContestantDetails>();
        Anonymous.Group = new GroupDef('row30', '', true);
        Anonymous.Name = 'Anonymous';
        Anonymous.FieldName = 'Anonymous';
        Anonymous.DefinedFor = 'Both';
        Anonymous.TranslationKey = 'racecontestantdetails.anonymous';
        Anonymous.Editable = true;
        Anonymous.Filterable = false;
        Anonymous.Sortable = true;
        Anonymous.CssClasses = '';
        Anonymous.value = (racecontestantdetails) => racecontestantdetails.Anonymous;
        Anonymous.setValue = (racecontestantdetails, value) => (racecontestantdetails.Anonymous = value);
        Anonymous.DetailEditor = markRaw(InputBool);
        Anonymous.DetailEditorProps = { label: 'racecontestantdetails.anonymous' };
        Anonymous.CellEditor = markRaw(InputBool);
        Anonymous.CellEditorProps = {};
        Anonymous.CellRenderer = markRaw(DisplayBool);
        Anonymous.CellRendererProps = {};
        Anonymous.Validator = null;
        Anonymous.SortDirection = undefined;
        Anonymous.cellClassRules = undefined;
        Anonymous.MinWidth = undefined;
        Anonymous.MaxWidth = undefined;
        Anonymous.Tooltip = 'racecontestantdetails.isanonymoustooltip';
        this.Fields.push(Anonymous);

        const infotext0 = new FieldDef<RaceContestantDetails>();
        infotext0.Group = new GroupDef('row30', '', true);
        infotext0.Name = 'infotext0';
        infotext0.FieldName = 'infotext0';
        infotext0.DefinedFor = 'Detail';
        infotext0.TranslationKey = '';
        infotext0.Editable = true;
        infotext0.Filterable = false;
        infotext0.Sortable = true;
        infotext0.CssClasses = '';
        infotext0.value = () => {
            return '';
        };
        infotext0.setValue = () => {};
        infotext0.DetailEditor = markRaw(DisplayInfotext);
        infotext0.DetailEditorProps = {};
        infotext0.Validator = null;
        infotext0.SortDirection = undefined;
        infotext0.cellClassRules = undefined;
        infotext0.MinWidth = undefined;
        infotext0.MaxWidth = undefined;
        infotext0.Tooltip = null;
        this.Fields.push(infotext0);

        const Address1 = new FieldDef<RaceContestantDetails>();
        Address1.Group = new GroupDef('row40', '', true);
        Address1.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        Address1.Name = 'Address1';
        Address1.FieldName = 'Address1';
        Address1.DefinedFor = 'Both';
        Address1.TranslationKey = 'racecontestantdetails.address1';
        Address1.Editable = true;
        Address1.Filterable = true;
        Address1.Sortable = true;
        Address1.CssClasses = '';
        Address1.value = (racecontestantdetails) => racecontestantdetails.Address1;
        Address1.setValue = (racecontestantdetails, value) => (racecontestantdetails.Address1 = value);
        Address1.DetailEditor = markRaw(InputText);
        Address1.DetailEditorProps = {};
        Address1.CellEditor = markRaw(InputText);
        Address1.CellEditorProps = {};
        Address1.CellRenderer = markRaw(ValidatedText);
        Address1.CellRendererProps = {};
        Address1.Validator = null;
        Address1.SortDirection = undefined;
        Address1.cellClassRules = undefined;
        Address1.MinWidth = undefined;
        Address1.MaxWidth = undefined;
        Address1.Tooltip = null;
        this.Fields.push(Address1);

        const Address2 = new FieldDef<RaceContestantDetails>();
        Address2.Group = new GroupDef('row40', '', true);
        Address2.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        Address2.Name = 'Address2';
        Address2.FieldName = 'Address2';
        Address2.DefinedFor = 'Both';
        Address2.TranslationKey = 'racecontestantdetails.address2';
        Address2.Editable = true;
        Address2.Filterable = true;
        Address2.Sortable = true;
        Address2.CssClasses = '';
        Address2.value = (racecontestantdetails) => racecontestantdetails.Address2;
        Address2.setValue = (racecontestantdetails, value) => (racecontestantdetails.Address2 = value);
        Address2.DetailEditor = markRaw(InputText);
        Address2.DetailEditorProps = {};
        Address2.CellEditor = markRaw(InputText);
        Address2.CellEditorProps = {};
        Address2.CellRenderer = markRaw(ValidatedText);
        Address2.CellRendererProps = {};
        Address2.Validator = null;
        Address2.SortDirection = undefined;
        Address2.cellClassRules = undefined;
        Address2.MinWidth = undefined;
        Address2.MaxWidth = undefined;
        Address2.Tooltip = null;
        this.Fields.push(Address2);

        const PostalNumber = new FieldDef<RaceContestantDetails>();
        PostalNumber.Group = new GroupDef('row50', '', true);
        PostalNumber.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        PostalNumber.Name = 'PostalNumber';
        PostalNumber.FieldName = 'PostalNumber';
        PostalNumber.DefinedFor = 'Both';
        PostalNumber.TranslationKey = 'racecontestantdetails.postalnumber';
        PostalNumber.Editable = true;
        PostalNumber.Filterable = true;
        PostalNumber.Sortable = true;
        PostalNumber.CssClasses = '';
        PostalNumber.value = (racecontestantdetails) => racecontestantdetails.PostalNumber;
        PostalNumber.setValue = (racecontestantdetails, value) => (racecontestantdetails.PostalNumber = value);
        PostalNumber.DetailEditor = markRaw(InputText);
        PostalNumber.DetailEditorProps = {};
        PostalNumber.CellEditor = markRaw(InputText);
        PostalNumber.CellEditorProps = {};
        PostalNumber.CellRenderer = markRaw(ValidatedText);
        PostalNumber.CellRendererProps = {};
        PostalNumber.Validator = null;
        PostalNumber.SortDirection = undefined;
        PostalNumber.cellClassRules = undefined;
        PostalNumber.MinWidth = undefined;
        PostalNumber.MaxWidth = undefined;
        PostalNumber.Tooltip = null;
        this.Fields.push(PostalNumber);

        const City = new FieldDef<RaceContestantDetails>();
        City.Group = new GroupDef('row50', '', true);
        City.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        City.Name = 'City';
        City.FieldName = 'City';
        City.DefinedFor = 'Both';
        City.TranslationKey = 'racecontestantdetails.city';
        City.Editable = true;
        City.Filterable = true;
        City.Sortable = true;
        City.CssClasses = '';
        City.value = (racecontestantdetails) => racecontestantdetails.City;
        City.setValue = (racecontestantdetails, value) => (racecontestantdetails.City = value);
        City.DetailEditor = markRaw(InputText);
        City.DetailEditorProps = {};
        City.CellEditor = markRaw(InputText);
        City.CellEditorProps = {};
        City.CellRenderer = markRaw(ValidatedText);
        City.CellRendererProps = {};
        City.Validator = null;
        City.SortDirection = undefined;
        City.cellClassRules = undefined;
        City.MinWidth = undefined;
        City.MaxWidth = undefined;
        City.Tooltip = null;
        this.Fields.push(City);

        const AddressCountryISO2 = new FieldDef<RaceContestantDetails>();
        AddressCountryISO2.Group = new GroupDef('row60', '', true);
        AddressCountryISO2.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        AddressCountryISO2.Name = 'AddressCountryISO2';
        AddressCountryISO2.FieldName = 'AddressCountryISO2';
        AddressCountryISO2.DefinedFor = 'Both';
        AddressCountryISO2.TranslationKey = 'racecontestantdetails.addresscountryiso2';
        AddressCountryISO2.Editable = true;
        AddressCountryISO2.Filterable = true;
        AddressCountryISO2.Sortable = true;
        AddressCountryISO2.CssClasses = '';
        AddressCountryISO2.value = (racecontestantdetails) => racecontestantdetails.AddressCountryISO2;
        AddressCountryISO2.setValue = (racecontestantdetails, value) => (racecontestantdetails.AddressCountryISO2 = value);
        AddressCountryISO2.DetailEditor = markRaw(SelectCountry);
        AddressCountryISO2.DetailEditorProps = {};
        AddressCountryISO2.CellEditor = markRaw(InputText);
        AddressCountryISO2.CellEditorProps = {};
        AddressCountryISO2.CellRenderer = markRaw(ValidatedText);
        AddressCountryISO2.CellRendererProps = {};
        AddressCountryISO2.Validator = null;
        AddressCountryISO2.SortDirection = undefined;
        AddressCountryISO2.cellClassRules = undefined;
        AddressCountryISO2.MinWidth = undefined;
        AddressCountryISO2.MaxWidth = undefined;
        AddressCountryISO2.Tooltip = null;
        this.Fields.push(AddressCountryISO2);

        const NationalityCountryID = new FieldDef<RaceContestantDetails>();
        NationalityCountryID.Group = new GroupDef('row60', '', true);
        NationalityCountryID.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        NationalityCountryID.Name = 'NationalityCountryID';
        NationalityCountryID.FieldName = 'NationalityCountryID';
        NationalityCountryID.DefinedFor = 'Both';
        NationalityCountryID.TranslationKey = 'racecontestantdetails.nationalitycountryid';
        NationalityCountryID.Editable = true;
        NationalityCountryID.Filterable = false;
        NationalityCountryID.Sortable = true;
        NationalityCountryID.CssClasses = '';
        NationalityCountryID.value = (racecontestantdetails) => racecontestantdetails.NationalityCountryID;
        NationalityCountryID.setValue = (racecontestantdetails, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            racecontestantdetails.NationalityCountryID = value;
        };
        NationalityCountryID.DetailEditor = markRaw(SelectCountry);
        NationalityCountryID.DetailEditorProps = {};
        NationalityCountryID.CellEditor = markRaw(InputInt);
        NationalityCountryID.CellEditorProps = {};
        NationalityCountryID.CellRenderer = markRaw(ValidatedInt);
        NationalityCountryID.CellRendererProps = {};
        NationalityCountryID.Validator = null;
        NationalityCountryID.SortDirection = undefined;
        NationalityCountryID.cellClassRules = undefined;
        NationalityCountryID.MinWidth = undefined;
        NationalityCountryID.MaxWidth = undefined;
        NationalityCountryID.Tooltip = null;
        this.Fields.push(NationalityCountryID);

        const Phone = new FieldDef<RaceContestantDetails>();
        Phone.Group = new GroupDef('row70', '', true);
        Phone.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        Phone.Name = 'Phone';
        Phone.FieldName = 'Phone';
        Phone.DefinedFor = 'Both';
        Phone.TranslationKey = 'racecontestantdetails.phone';
        Phone.Editable = true;
        Phone.Filterable = true;
        Phone.Sortable = true;
        Phone.CssClasses = '';
        Phone.value = (racecontestantdetails) => racecontestantdetails.Phone;
        Phone.setValue = (racecontestantdetails, value) => (racecontestantdetails.Phone = value);
        Phone.DetailEditor = markRaw(InputPhone);
        Phone.DetailEditorProps = {};
        Phone.CellEditor = markRaw(InputText);
        Phone.CellEditorProps = {};
        Phone.CellRenderer = markRaw(ValidatedText);
        Phone.CellRendererProps = {};
        Phone.Validator = null;
        Phone.SortDirection = undefined;
        Phone.cellClassRules = undefined;
        Phone.MinWidth = undefined;
        Phone.MaxWidth = undefined;
        Phone.Tooltip = null;
        this.Fields.push(Phone);

        const Email = new FieldDef<RaceContestantDetails>();
        Email.Group = new GroupDef('row70', '', true);
        Email.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        Email.Name = 'Email';
        Email.FieldName = 'Email';
        Email.DefinedFor = 'Both';
        Email.TranslationKey = 'racecontestantdetails.email';
        Email.Editable = true;
        Email.Filterable = true;
        Email.Sortable = true;
        Email.CssClasses = '';
        Email.value = (racecontestantdetails) => racecontestantdetails.Email;
        Email.setValue = (racecontestantdetails, value) => (racecontestantdetails.Email = value);
        Email.DetailEditor = markRaw(InputEmail);
        Email.DetailEditorProps = {};
        Email.CellEditor = markRaw(InputText);
        Email.CellEditorProps = {};
        Email.CellRenderer = markRaw(ValidatedText);
        Email.CellRendererProps = {};
        Email.Validator = null;
        Email.SortDirection = undefined;
        Email.cellClassRules = undefined;
        Email.MinWidth = undefined;
        Email.MaxWidth = undefined;
        Email.Tooltip = null;
        this.Fields.push(Email);

        const separator1 = new FieldDef<RaceContestantDetails>();
        separator1.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        separator1.Name = 'separator1';
        separator1.FieldName = 'separator1';
        separator1.DefinedFor = 'Detail';
        separator1.TranslationKey = 'general.nextofkinguide';
        separator1.Editable = true;
        separator1.Filterable = false;
        separator1.Sortable = true;
        separator1.CssClasses = '';
        separator1.value = () => {
            return 'general.nextofkinguide';
        };
        separator1.setValue = () => {};
        separator1.DetailEditor = markRaw(DisplaySeparator);
        separator1.DetailEditorProps = {};
        separator1.CellEditor = markRaw(DisplaySeparator);
        separator1.CellEditorProps = {};
        separator1.CellRenderer = markRaw(DisplaySeparator);
        separator1.CellRendererProps = {};
        separator1.Validator = null;
        separator1.SortDirection = undefined;
        separator1.cellClassRules = undefined;
        separator1.MinWidth = undefined;
        separator1.MaxWidth = undefined;
        separator1.Tooltip = null;
        this.Fields.push(separator1);

        const NextOfKin = new FieldDef<RaceContestantDetails>();
        NextOfKin.Group = new GroupDef('row80', '', true);
        NextOfKin.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        NextOfKin.Name = 'NextOfKin';
        NextOfKin.FieldName = 'NextOfKin';
        NextOfKin.DefinedFor = 'Both';
        NextOfKin.TranslationKey = 'racecontestantdetails.nextofkin';
        NextOfKin.Editable = true;
        NextOfKin.Filterable = true;
        NextOfKin.Sortable = true;
        NextOfKin.CssClasses = '';
        NextOfKin.value = (racecontestantdetails) => racecontestantdetails.NextOfKin;
        NextOfKin.setValue = (racecontestantdetails, value) => (racecontestantdetails.NextOfKin = value);
        NextOfKin.DetailEditor = markRaw(InputText);
        NextOfKin.DetailEditorProps = {};
        NextOfKin.CellEditor = markRaw(InputText);
        NextOfKin.CellEditorProps = {};
        NextOfKin.CellRenderer = markRaw(ValidatedText);
        NextOfKin.CellRendererProps = {};
        NextOfKin.Validator = null;
        NextOfKin.SortDirection = undefined;
        NextOfKin.cellClassRules = undefined;
        NextOfKin.MinWidth = undefined;
        NextOfKin.MaxWidth = undefined;
        NextOfKin.Tooltip = null;
        this.Fields.push(NextOfKin);

        const NextOfKinPhone = new FieldDef<RaceContestantDetails>();
        NextOfKinPhone.Group = new GroupDef('row80', '', true);
        NextOfKinPhone.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        NextOfKinPhone.Name = 'NextOfKinPhone';
        NextOfKinPhone.FieldName = 'NextOfKinPhone';
        NextOfKinPhone.DefinedFor = 'Both';
        NextOfKinPhone.TranslationKey = 'racecontestantdetails.nextofkinphone';
        NextOfKinPhone.Editable = true;
        NextOfKinPhone.Filterable = true;
        NextOfKinPhone.Sortable = true;
        NextOfKinPhone.CssClasses = '';
        NextOfKinPhone.value = (racecontestantdetails) => racecontestantdetails.NextOfKinPhone;
        NextOfKinPhone.setValue = (racecontestantdetails, value) => (racecontestantdetails.NextOfKinPhone = value);
        NextOfKinPhone.DetailEditor = markRaw(InputPhone);
        NextOfKinPhone.DetailEditorProps = {};
        NextOfKinPhone.CellEditor = markRaw(InputText);
        NextOfKinPhone.CellEditorProps = {};
        NextOfKinPhone.CellRenderer = markRaw(ValidatedText);
        NextOfKinPhone.CellRendererProps = {};
        NextOfKinPhone.Validator = null;
        NextOfKinPhone.SortDirection = undefined;
        NextOfKinPhone.cellClassRules = undefined;
        NextOfKinPhone.MinWidth = undefined;
        NextOfKinPhone.MaxWidth = undefined;
        NextOfKinPhone.Tooltip = null;
        this.Fields.push(NextOfKinPhone);

        const GuideName = new FieldDef<RaceContestantDetails>();
        GuideName.Group = new GroupDef('row90', '', true);
        GuideName.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        GuideName.Name = 'GuideName';
        GuideName.FieldName = 'GuideName';
        GuideName.DefinedFor = 'Both';
        GuideName.TranslationKey = 'racecontestantdetails.guidename';
        GuideName.Editable = true;
        GuideName.Filterable = true;
        GuideName.Sortable = true;
        GuideName.CssClasses = '';
        GuideName.value = (racecontestantdetails) => racecontestantdetails.GuideName;
        GuideName.setValue = (racecontestantdetails, value) => (racecontestantdetails.GuideName = value);
        GuideName.DetailEditor = markRaw(InputText);
        GuideName.DetailEditorProps = {};
        GuideName.CellEditor = markRaw(InputText);
        GuideName.CellEditorProps = {};
        GuideName.CellRenderer = markRaw(ValidatedText);
        GuideName.CellRendererProps = {};
        GuideName.Validator = null;
        GuideName.SortDirection = undefined;
        GuideName.cellClassRules = undefined;
        GuideName.MinWidth = undefined;
        GuideName.MaxWidth = undefined;
        GuideName.Tooltip = null;
        this.Fields.push(GuideName);

        const infotext2 = new FieldDef<RaceContestantDetails>();
        infotext2.Group = new GroupDef('row90', '', true);
        infotext2.Collapsable = new CollapsableDef('contestantaddress', 'racecontestantdetails.address');
        infotext2.Name = 'infotext2';
        infotext2.FieldName = 'infotext2';
        infotext2.DefinedFor = 'Detail';
        infotext2.TranslationKey = '';
        infotext2.Editable = true;
        infotext2.Filterable = false;
        infotext2.Sortable = true;
        infotext2.CssClasses = '';
        infotext2.value = () => {
            return '';
        };
        infotext2.setValue = () => {};
        infotext2.DetailEditor = markRaw(DisplayInfotext);
        infotext2.DetailEditorProps = {};
        infotext2.Validator = null;
        infotext2.SortDirection = undefined;
        infotext2.cellClassRules = undefined;
        infotext2.MinWidth = undefined;
        infotext2.MaxWidth = undefined;
        infotext2.Tooltip = null;
        this.Fields.push(infotext2);

        const separator3 = new FieldDef<RaceContestantDetails>();
        separator3.Name = 'separator3';
        separator3.FieldName = 'separator3';
        separator3.DefinedFor = 'Detail';
        separator3.TranslationKey = 'general.contestant';
        separator3.Editable = true;
        separator3.Filterable = false;
        separator3.Sortable = true;
        separator3.CssClasses = '';
        separator3.value = () => {
            return 'general.contestant';
        };
        separator3.setValue = () => {};
        separator3.DetailEditor = markRaw(DisplaySeparator);
        separator3.DetailEditorProps = {};
        separator3.CellEditor = markRaw(DisplaySeparator);
        separator3.CellEditorProps = {};
        separator3.CellRenderer = markRaw(DisplaySeparator);
        separator3.CellRendererProps = {};
        separator3.Validator = null;
        separator3.SortDirection = undefined;
        separator3.cellClassRules = undefined;
        separator3.MinWidth = undefined;
        separator3.MaxWidth = undefined;
        separator3.Tooltip = null;
        this.Fields.push(separator3);

        const ClubID = new FieldDef<RaceContestantDetails>();
        ClubID.Group = new GroupDef('row100', '', true);
        ClubID.Name = 'ClubID';
        ClubID.FieldName = 'ClubID';
        ClubID.DefinedFor = 'Both';
        ClubID.TranslationKey = 'racecontestantdetails.clubid';
        ClubID.Editable = true;
        ClubID.Filterable = false;
        ClubID.Sortable = true;
        ClubID.CssClasses = '';
        ClubID.value = (racecontestantdetails) => racecontestantdetails.ClubID;
        ClubID.setValue = (racecontestantdetails, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantdetails.ClubID = value;
        };
        ClubID.DetailEditor = markRaw(InputSelect);
        ClubID.DetailEditorProps = { showSearch: true, virtualScrollerMaxItems: 30, datasource: { mapsource: ClubsSource } };
        ClubID.Validator = null;
        ClubID.SortDirection = undefined;
        ClubID.cellClassRules = undefined;
        ClubID.MinWidth = undefined;
        ClubID.MaxWidth = undefined;
        ClubID.Tooltip = null;
        this.Fields.push(ClubID);

        const TeamID = new FieldDef<RaceContestantDetails>();
        TeamID.Group = new GroupDef('row100', '', true);
        TeamID.Name = 'TeamID';
        TeamID.FieldName = 'TeamID';
        TeamID.DefinedFor = 'Both';
        TeamID.TranslationKey = 'racecontestantdetails.teamid';
        TeamID.Editable = true;
        TeamID.Filterable = false;
        TeamID.Sortable = true;
        TeamID.CssClasses = '';
        TeamID.value = (racecontestantdetails) => racecontestantdetails.TeamID;
        TeamID.setValue = (racecontestantdetails, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantdetails.TeamID = value;
        };
        TeamID.DetailEditor = markRaw(InputSelect);
        TeamID.DetailEditorProps = { showSearch: true, virtualScrollerMaxItems: 30, datasource: { mapsource: TeamsSource } };
        TeamID.Validator = null;
        TeamID.SortDirection = undefined;
        TeamID.cellClassRules = undefined;
        TeamID.MinWidth = undefined;
        TeamID.MaxWidth = undefined;
        TeamID.Tooltip = null;
        this.Fields.push(TeamID);

        const CompanyClub = new FieldDef<RaceContestantDetails>();
        CompanyClub.Group = new GroupDef('row110', '', true);
        CompanyClub.Name = 'CompanyClub';
        CompanyClub.FieldName = 'CompanyClub';
        CompanyClub.DefinedFor = 'Both';
        CompanyClub.TranslationKey = 'racecontestantdetails.companyclub';
        CompanyClub.Editable = true;
        CompanyClub.Filterable = true;
        CompanyClub.Sortable = true;
        CompanyClub.CssClasses = '';
        CompanyClub.value = (racecontestantdetails) => racecontestantdetails.CompanyClub;
        CompanyClub.setValue = (racecontestantdetails, value) => (racecontestantdetails.CompanyClub = value);
        CompanyClub.DetailEditor = markRaw(InputText);
        CompanyClub.DetailEditorProps = {};
        CompanyClub.CellEditor = markRaw(InputText);
        CompanyClub.CellEditorProps = {};
        CompanyClub.CellRenderer = markRaw(ValidatedText);
        CompanyClub.CellRendererProps = {};
        CompanyClub.Validator = null;
        CompanyClub.SortDirection = undefined;
        CompanyClub.cellClassRules = undefined;
        CompanyClub.MinWidth = undefined;
        CompanyClub.MaxWidth = undefined;
        CompanyClub.Tooltip = null;
        this.Fields.push(CompanyClub);

        const EventTeamId = new FieldDef<RaceContestantDetails>();
        EventTeamId.Group = new GroupDef('row110', '', true);
        EventTeamId.Name = 'EventTeamId';
        EventTeamId.FieldName = 'EventTeamId';
        EventTeamId.DefinedFor = 'Both';
        EventTeamId.TranslationKey = 'racecontestantdetails.eventteamid';
        EventTeamId.Editable = true;
        EventTeamId.Filterable = false;
        EventTeamId.Sortable = true;
        EventTeamId.CssClasses = '';
        EventTeamId.value = (racecontestantdetails) => racecontestantdetails.EventTeamId;
        EventTeamId.setValue = (racecontestantdetails, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantdetails.EventTeamId = value;
        };
        EventTeamId.DetailEditor = markRaw(SelectEventTeam);
        EventTeamId.DetailEditorProps = { datasource: { mapsource: EventTeamsSource } };
        EventTeamId.Validator = null;
        EventTeamId.SortDirection = undefined;
        EventTeamId.cellClassRules = undefined;
        EventTeamId.MinWidth = undefined;
        EventTeamId.MaxWidth = undefined;
        EventTeamId.Tooltip = 'racecontestantdetails.eventteamtooltip';
        this.Fields.push(EventTeamId);

        const CategoryIDValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const CategoryID = new FieldDef<RaceContestantDetails>();
        CategoryID.Group = new GroupDef('row120', '', true);
        CategoryID.Name = 'CategoryID';
        CategoryID.FieldName = 'CategoryID';
        CategoryID.DefinedFor = 'Both';
        CategoryID.TranslationKey = 'racecontestantdetails.categoryid';
        CategoryID.Editable = true;
        CategoryID.Filterable = false;
        CategoryID.Sortable = true;
        CategoryID.CssClasses = '';
        CategoryID.value = (racecontestantdetails) => racecontestantdetails.CategoryID;
        CategoryID.setValue = (racecontestantdetails, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantdetails.CategoryID = value;
        };
        CategoryID.DetailEditor = markRaw(InputSelect);
        CategoryID.DetailEditorProps = { showSearch: true, virtualScrollerMaxItems: 30, datasource: { mapsource: CategoriesSource } };
        CategoryID.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        CategoryID.SortDirection = undefined;
        CategoryID.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(CategoryIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(CategoryIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        CategoryID.MinWidth = undefined;
        CategoryID.MaxWidth = undefined;
        CategoryID.Tooltip = null;
        this.Fields.push(CategoryID);

        const ContestantStatusID = new FieldDef<RaceContestantDetails>();
        ContestantStatusID.Group = new GroupDef('row120', '', true);
        ContestantStatusID.Name = 'ContestantStatusID';
        ContestantStatusID.FieldName = 'ContestantStatusID';
        ContestantStatusID.DefinedFor = 'Both';
        ContestantStatusID.TranslationKey = 'racecontestantdetails.contestantstatusid';
        ContestantStatusID.Editable = true;
        ContestantStatusID.Filterable = true;
        ContestantStatusID.Sortable = true;
        ContestantStatusID.CssClasses = '';
        ContestantStatusID.value = (racecontestantdetails) => racecontestantdetails.ContestantStatusID;
        ContestantStatusID.setValue = (racecontestantdetails, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            racecontestantdetails.ContestantStatusID = value;
        };
        ContestantStatusID.DetailEditor = markRaw(InputSelect);
        ContestantStatusID.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: ContestantStatusSource } };
        ContestantStatusID.CellEditor = markRaw(InputSelect);
        ContestantStatusID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: ContestantStatusSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        ContestantStatusID.CellRenderer = markRaw(DataSourceLookup);
        ContestantStatusID.CellRendererProps = { datasource: { mapsource: ContestantStatusSource } };
        ContestantStatusID.ExportValueFormatter = (obj: RaceContestantDetails) => {
            return ContestantStatusSource.value.get(obj.ContestantStatusID)?.text;
        };
        ContestantStatusID.Validator = null;
        ContestantStatusID.SortDirection = undefined;
        ContestantStatusID.cellClassRules = undefined;
        ContestantStatusID.MinWidth = undefined;
        ContestantStatusID.MaxWidth = undefined;
        ContestantStatusID.Tooltip = null;
        this.Fields.push(ContestantStatusID);

        const infotext4 = new FieldDef<RaceContestantDetails>();
        infotext4.Group = new GroupDef('row130', '', true);
        infotext4.Name = 'infotext4';
        infotext4.FieldName = 'infotext4';
        infotext4.DefinedFor = 'Detail';
        infotext4.TranslationKey = '';
        infotext4.Editable = true;
        infotext4.Filterable = false;
        infotext4.Sortable = true;
        infotext4.CssClasses = '';
        infotext4.value = () => {
            return '';
        };
        infotext4.setValue = () => {};
        infotext4.DetailEditor = markRaw(DisplayInfotext);
        infotext4.DetailEditorProps = {};
        infotext4.Validator = null;
        infotext4.SortDirection = undefined;
        infotext4.cellClassRules = undefined;
        infotext4.MinWidth = undefined;
        infotext4.MaxWidth = undefined;
        infotext4.Tooltip = null;
        this.Fields.push(infotext4);

        const LicenceID = new FieldDef<RaceContestantDetails>();
        LicenceID.Group = new GroupDef('row130', '', true);
        LicenceID.Name = 'LicenceID';
        LicenceID.FieldName = 'LicenceID';
        LicenceID.DefinedFor = 'Both';
        LicenceID.TranslationKey = 'racecontestantdetails.licenceid';
        LicenceID.Editable = true;
        LicenceID.Filterable = false;
        LicenceID.Sortable = true;
        LicenceID.CssClasses = '';
        LicenceID.value = (racecontestantdetails) => racecontestantdetails.LicenceID;
        LicenceID.setValue = (racecontestantdetails, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantdetails.LicenceID = value;
        };
        LicenceID.DetailEditor = markRaw(InputSelect);
        LicenceID.DetailEditorProps = { showSearch: false, virtualScrollerMaxItems: 30, datasource: { mapsource: LicencesSource } };
        LicenceID.Validator = null;
        LicenceID.SortDirection = undefined;
        LicenceID.cellClassRules = undefined;
        LicenceID.MinWidth = undefined;
        LicenceID.MaxWidth = undefined;
        LicenceID.Tooltip = null;
        this.Fields.push(LicenceID);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Genders']);
        await useCommonStore().EnsureLoaded(['Clubs']);
        await useCommonStore().EnsureLoaded(['Teams']);
        await useEventCommonStore().EnsureLoaded(['EventTeams']);
        await useEventCommonStore().EnsureLoaded(['Categories']);
        await useCommonStore().EnsureLoaded(['ContestantStatus']);
        await useEventCommonStore().EnsureLoaded(['Licences']);
    }
}
